package nl.appstuff.padelchecker.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import nl.appstuff.padelchecker.site.PageSeo
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.components.NavBar
import nl.appstuff.padelchecker.site.components.sections.FooterSection
import nl.appstuff.padelchecker.site.localization.LocalSiteLanguage
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@Page
@Composable
fun PrivacyPage() {
    val seoStrings = LocalSiteStrings.current
    val language = LocalSiteLanguage.current
    PageSeo(
        title = seoStrings.privacySeoTitle,
        description = seoStrings.privacySeoDescription,
        canonicalPath = "/privacy",
        lang = language.tag,
    )

    Column(modifier = Modifier.fillMaxWidth().minHeight(100.vh)) {
        NavBar()
        PrivacyContent()
        FooterSection()
    }
}

@Composable
private fun PrivacyContent() {
    val strings = LocalSiteStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("flex", "1") }
            .background(SiteColors.backgroundLight)
            .padding(topBottom = 64.px, leftRight = 24.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(760.px),
            verticalArrangement = Arrangement.spacedBy(24.px),
        ) {
            SpanText(
                strings.privacyTitle,
                modifier = Modifier
                    .fontSize(40.px)
                    .fontWeight(FontWeight.Bold)
                    .color(SiteColors.textLight),
            )
            SpanText(
                strings.privacyLastUpdated,
                modifier = Modifier
                    .fontSize(14.px)
                    .color(SiteColors.textMutedLight),
            )
            SpanText(
                strings.privacyEnglishBelow,
                modifier = Modifier
                    .fontSize(13.px)
                    .color(SiteColors.textMutedLight)
                    .styleModifier { property("font-style", "italic") },
            )

            PrivacyBlock(
                title = "1. WIE WIJ ZIJN",
                text = """
            Padelchecker ("wij", "ons", "onze") is een mobiele app die je helpt beschikbare
            tijdsloten voor padelbanen te vinden en te volgen op meerdere boekingsplatforms.

            Contact: contact@padelchecker.nl
                """.trimIndent(),
            )
            PrivacyBlock(
                id = "analytics",
                title = "2. WELKE GEGEVENS WIJ VERZAMELEN EN WAAROM",
                text = """
            a) Accountgegevens
               - E-mailadres en wachtwoord — vereist om je account aan te maken en te beheren.
               - Wij gebruiken Supabase (supabase.com) als onze authenticatie- en databaseprovider. Je
                 inloggegevens worden veilig opgeslagen door Supabase en zijn nooit rechtstreeks voor ons toegankelijk.

            b) Meldingswatches
               - Wanneer je een watch aanmaakt, slaan wij je zoekvoorkeuren op: geselecteerde clubs,
                 baanlengte, baanformaat (single/dubbel), type overdekking (binnen/buiten/overdekt),
                 datum/data of dag(en) van de week.
               - Deze gegevens worden op onze server opgeslagen en uitsluitend gebruikt om
                 beschikbaarheid van banen te controleren en pushmeldingen te sturen wanneer er
                 overeenkomende tijdsloten worden gevonden.

            c) Apparaattoken
               - Wanneer je meldingen inschakelt, wordt het Firebase Cloud Messaging (FCM)-token van
                 je apparaat op onze server opgeslagen en gekoppeld aan je account. Dit is nodig om
                 pushmeldingen naar je apparaat te kunnen versturen.
               - FCM wordt geleverd door Google. Het privacybeleid van Google is van toepassing op de
                 levering van meldingen: https://policies.google.com/privacy

            d) Zoekvoorkeuren
               - Je laatst gebruikte zoekvoorkeuren (duur, baanformaat, overdekking, locatiequery en
                 straal) worden lokaal op je apparaat opgeslagen zodat je instellingen tussen sessies
                 behouden blijven. Deze gegevens verlaten je apparaat nooit.

            e) Locatiegegevens (optioneel)
               - Op mobiel (iOS/Android) kun je optioneel locatietoegang verlenen. Als je dit doet,
                 wordt je laatst bekende GPS-locatie gebruikt om het zoekveld automatisch in te vullen
                 en om banen in de buurt te zoeken.
               - De coördinaten worden naar onze server gestuurd als onderdeel van je zoekopdracht en
                 worden via PDOK (api.pdok.nl) omgezet naar een adres. Ze worden niet apart opgeslagen.
               - Op het web is locatietoegang niet beschikbaar; je voert altijd handmatig een adres in.
               - Je kunt locatietoegang op elk moment intrekken via de instellingen van je apparaat.

            f) Serverlogs
               - Onze server registreert standaard HTTP-toegangslogs, waaronder IP-adressen,
                 tijdstempels van verzoeken en bezochte endpoints. Deze logs worden gebruikt voor
                 beveiligingsmonitoring en probleemoplossing en worden niet gedeeld met derden.

            g) Abonnementsgegevens
               - Om je abonnementsstatus bij te houden delen wij je gebruikersidentificatie en
                 e-mailadres met RevenueCat (revenuecat.com). RevenueCat slaat je aankoopgeschiedenis
                 op en koppelt deze aan jouw account.
               - Betalingen zelf worden verwerkt door Apple (iOS), Google (Android) of Paddle (web),
                 afhankelijk van het platform waarop je hebt gekocht. Padelchecker heeft geen directe
                 toegang tot je betaalgegevens.

            h) Analytics en crashrapportage
               - Wij gebruiken Firebase Analytics om anoniem inzicht te krijgen in welke functies
                 worden gebruikt en hoe de app beter kan worden. Dit omvat schermnamen en
                 gebruikersacties, maar bevat nooit persoonlijk identificeerbare informatie.
               - Analytics worden alleen verzameld als je hier expliciet toestemming voor geeft. Op
                 mobiel (iOS/Android) wordt dit gevraagd tijdens het onboardingproces; op het web via
                 een klein toestemmingsbericht. Je kunt je keuze op elk moment wijzigen in de
                 instellingen.
               - Wij gebruiken Firebase Crashlytics om crashrapporten te ontvangen. Dit helpt ons om
                 technische problemen snel op te sporen en op te lossen. Crashrapporten bevatten
                 technische apparaatinformatie en de toestand van de app op het moment van de crash,
                 maar geen persoonlijke gegevens.
               - Om crashes en analyticsgegevens te kunnen groeperen per gebruiker zonder je identiteit
                 te kennen, gebruiken wij een anonieme identificatie:
                 • Als je bent ingelogd: een onherleidbare hash (SHA-256) van je gebruikers-ID.
                   Deze hash kan niet worden teruggerekend naar je account.
                 • Als je niet bent ingelogd: een willekeurig gegenereerd apparaat-ID dat lokaal
                   wordt opgeslagen.
               - Wij gebruiken deze gegevens uitsluitend om de app te verbeteren en problemen op te
                 lossen. Er worden geen analytics of crashgegevens gedeeld met derden voor commerciële
                 doeleinden.

                """.trimIndent(),
            )
            PrivacyBlock(
                title = "3. GEGEVENS DIE WIJ NIET VERZAMELEN",
                text = """
            - Wij slaan je GPS-locatie NIET permanent op. Locatiegegevens worden alleen gebruikt
              tijdens een zoekopdracht als je hier toestemming voor hebt gegeven (zie sectie 2e).
            - Wij gebruiken GEEN advertenties of advertentie-ID's.
            - Wij hebben GEEN toegang tot je contacten, agenda, foto's, camera of microfoon.
            - Wij verkopen je gegevens NIET aan derden.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "4. DIENSTEN VAN DERDEN",
                text = """
            Om de functionaliteit van de app te kunnen leveren, kunnen je gegevens worden verwerkt
            door de volgende diensten:

            - Supabase (supabase.com) — authenticatie en databasehosting. Slaat je accountgegevens en
              meldingswatches op. Gegevens worden opgeslagen in de EU (AWS eu-central-1).
            - Google Firebase Cloud Messaging — levering van pushmeldingen. Je FCM-apparaattoken wordt
              naar de servers van Google verzonden wanneer een melding naar je wordt verstuurd.
            - Google Firebase Analytics — anonieme gebruiksstatistieken (alleen na jouw toestemming).
              Privacybeleid: https://firebase.google.com/support/privacy
            - Google Firebase Crashlytics — crashrapportage met anonieme apparaat- en sessiegegevens.
            - RevenueCat (revenuecat.com) — abonnementsbeheer. Je gebruikersidentificatie en
              e-mailadres worden gedeeld met RevenueCat om je abonnementsstatus bij te houden.
            - Paddle (paddle.com) — betalingsverwerking voor webabonnementen. Paddle verwerkt je
              betaalgegevens bij aankopen via het web. Privacybeleid: https://www.paddle.com/legal/privacy
            - PDOK Locatieserver (api.pdok.nl) — een Nederlandse geocoderings-API van de overheid.
              Wanneer je een locatie invoert voor een zoekopdracht, wordt de adresregel naar PDOK
              gestuurd om coördinaten op te halen. Er worden geen persoonlijke identificatiegegevens
              meegestuurd in deze verzoeken.
            - Padelboekingsplatforms (bijv. Playtomic, Bookaball, MeetAndPlay) — je zoekcriteria
              (data, tijden, baanvoorkeuren) worden server-side doorgestuurd naar deze platforms om
              beschikbaarheid op te halen. Er worden geen accountgegevens gedeeld met deze platforms.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "5. BEWAARTERMIJN VAN GEGEVENS",
                text = """
            - Je accountgegevens en meldingswatches worden bewaard totdat je je account verwijdert.
            - Je kunt je account en alle bijbehorende gegevens op elk moment verwijderen via
              Instellingen → Account verwijderen in de app.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "6. JOUW RECHTEN",
                text = """
            Onder de AVG heb je het recht om je persoonsgegevens in te zien, te corrigeren of te
            verwijderen.
            Je kunt:
            - Je meldingswatches rechtstreeks in de app bekijken en bewerken.
            - Je account en alle bijbehorende gegevens verwijderen via Instellingen → Account verwijderen.
            - Contact met ons opnemen via contact@padelchecker.nl voor andere verzoeken met betrekking tot je gegevens.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "7. BEVEILIGING",
                text = """
            Alle communicatie tussen de app en onze server maakt gebruik van HTTPS/TLS-versleuteling.
            Authenticatietokens worden veilig opgeslagen met behulp van de platform-keychain (iOS) of
            Keystore (Android).
                """.trimIndent()
            )
            PrivacyBlock(
                title = "8. WIJZIGINGEN IN DIT BELEID",
                text = """
            Wij kunnen dit privacybeleid bijwerken wanneer de app verandert. De datum "Laatst
            bijgewerkt" bovenaan dit document geeft altijd de meest recente wijziging weer.
            Voortgezet gebruik van de app na wijzigingen betekent dat je het bijgewerkte beleid
            accepteert.
                """.trimIndent()
            )

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("border-top", "2px solid")
                        property("border-color", "rgba(0,0,0,0.12)")
                        property("margin-top", "16px")
                        property("margin-bottom", "16px")
                    }
            )

            SpanText(
                "Privacy Policy",
                modifier = Modifier
                    .fontSize(40.px)
                    .fontWeight(FontWeight.Bold)
                    .color(SiteColors.textLight),
            )
            SpanText(
                "Last updated: 17 May 2026",
                modifier = Modifier
                    .fontSize(14.px)
                    .color(SiteColors.textMutedLight),
            )

            PrivacyBlock(
                title = "1. WHO WE ARE",
                text = """
            Padelchecker ("we", "us", "our") is a mobile app that helps you find and track
            available time slots for padel courts across multiple booking platforms.

            Contact: contact@padelchecker.nl
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "2. WHAT DATA WE COLLECT AND WHY",
                text = """
            a) Account data
               - Email address and password — required to create and manage your account.
               - We use Supabase (supabase.com) as our authentication and database provider. Your
                 login credentials are stored securely by Supabase and are never directly accessible
                 to us.

            b) Notification watches
               - When you create a watch, we store your search preferences: selected clubs,
                 court length, court format (singles/doubles), cover type (indoor/outdoor/covered),
                 date(s) or day(s) of the week.
               - This data is stored on our server and used solely to check court availability and
                 send push notifications when matching time slots are found.

            c) Device token
               - When you enable notifications, your device's Firebase Cloud Messaging (FCM) token
                 is stored on our server and linked to your account. This is necessary to send push
                 notifications to your device.
               - FCM is provided by Google. Google's privacy policy applies to the delivery of
                 notifications: https://policies.google.com/privacy

            d) Search preferences
               - Your most recently used search preferences (duration, court format, cover type,
                 location query and radius) are stored locally on your device so your settings are
                 preserved between sessions. This data never leaves your device.

            e) Location data (optional)
               - On mobile (iOS/Android) you can optionally grant location access. If you do, your
                 last known GPS location is used to automatically fill in the search field and to
                 find nearby courts.
               - The coordinates are sent to our server as part of your search query and converted
                 to an address via PDOK (api.pdok.nl). They are not stored separately.
               - On the web, location access is not available; you always enter an address manually.
               - You can revoke location access at any time via your device settings.

            f) Server logs
               - Our server records standard HTTP access logs, including IP addresses, request
                 timestamps, and endpoints visited. These logs are used for security monitoring and
                 troubleshooting and are not shared with third parties.

            g) Subscription data
               - To track your subscription status, we share your user identifier and email address
                 with RevenueCat (revenuecat.com). RevenueCat stores your purchase history and
                 links it to your account.
               - Payments themselves are processed by Apple (iOS), Google (Android), or Paddle
                 (web), depending on the platform on which you purchased. Padelchecker does not
                 have direct access to your payment details.

            h) Analytics and crash reporting
               - We use Firebase Analytics to gain anonymous insight into which features are used
                 and how the app can be improved. This includes screen names and user actions, but
                 never contains personally identifiable information.
               - Analytics are only collected if you explicitly consent to this. On mobile
                 (iOS/Android) this is requested during the onboarding process; on the web via a
                 small consent message. You can change your choice at any time in the settings.
               - We use Firebase Crashlytics to receive crash reports. This helps us quickly
                 identify and resolve technical issues. Crash reports contain technical device
                 information and the state of the app at the time of the crash, but no personal data.
               - To group crashes and analytics data per user without knowing your identity, we use
                 an anonymous identifier:
                 • If you are logged in: an irreversible hash (SHA-256) of your user ID.
                   This hash cannot be traced back to your account.
                 • If you are not logged in: a randomly generated device ID that is stored locally.
               - We use this data solely to improve the app and resolve issues. No analytics or
                 crash data is shared with third parties for commercial purposes.

                """.trimIndent(),
            )
            PrivacyBlock(
                title = "3. DATA WE DO NOT COLLECT",
                text = """
            - We do NOT permanently store your GPS location. Location data is only used during a
              search if you have granted permission (see section 2e).
            - We do NOT use advertising or advertising IDs.
            - We do NOT have access to your contacts, calendar, photos, camera, or microphone.
            - We do NOT sell your data to third parties.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "4. THIRD-PARTY SERVICES",
                text = """
            To deliver the app's functionality, your data may be processed by the following services:

            - Supabase (supabase.com) — authentication and database hosting. Stores your account
              data and notification watches. Data is stored in the EU (AWS eu-central-1).
            - Google Firebase Cloud Messaging — delivery of push notifications. Your FCM device
              token is sent to Google's servers when a notification is sent to you.
            - Google Firebase Analytics — anonymous usage statistics (only with your consent).
              Privacy policy: https://firebase.google.com/support/privacy
            - Google Firebase Crashlytics — crash reporting with anonymous device and session data.
            - RevenueCat (revenuecat.com) — subscription management. Your user identifier and
              email address are shared with RevenueCat to track your subscription status.
            - Paddle (paddle.com) — payment processing for web subscriptions. Paddle processes your
              payment details for purchases made via the web. Privacy policy: https://www.paddle.com/legal/privacy
            - PDOK Location Server (api.pdok.nl) — a Dutch government geocoding API. When you
              enter a location for a search query, the address line is sent to PDOK to retrieve
              coordinates. No personally identifiable information is included in these requests.
            - Padel booking platforms (e.g. Playtomic, Bookaball, MeetAndPlay) — your search
              criteria (dates, times, court preferences) are forwarded server-side to these
              platforms to retrieve availability. No account data is shared with these platforms.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "5. DATA RETENTION",
                text = """
            - Your account data and notification watches are retained until you delete your account.
            - You can delete your account and all associated data at any time via
              Settings → Delete account in the app.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "6. YOUR RIGHTS",
                text = """
            Under the GDPR you have the right to access, correct, or delete your personal data.
            You can:
            - View and edit your notification watches directly in the app.
            - Delete your account and all associated data via Settings → Delete account.
            - Contact us at contact@padelchecker.nl for other requests regarding your data.
                """.trimIndent(),
            )
            PrivacyBlock(
                title = "7. SECURITY",
                text = """
            All communication between the app and our server uses HTTPS/TLS encryption.
            Authentication tokens are stored securely using the platform keychain (iOS) or
            Keystore (Android).
                """.trimIndent()
            )
            PrivacyBlock(
                title = "8. CHANGES TO THIS POLICY",
                text = """
            We may update this privacy policy when the app changes. The "Last updated" date at the
            top of this document always reflects the most recent change. Continued use of the app
            after changes means you accept the updated policy.
                """.trimIndent()
            )
        }
    }
}

@Composable
private fun PrivacyBlock(title: String, text: String, id: String? = null) {
    Column(
        verticalArrangement = Arrangement.spacedBy(8.px),
        modifier = if (id != null) Modifier.id(id) else Modifier,
    ) {
        SpanText(
            title,
            modifier = Modifier
                .fontSize(20.px)
                .fontWeight(FontWeight.SemiBold)
                .color(SiteColors.textLight),
        )
        PrivacyRichText(text)
    }
}

@Composable
private fun PrivacyRichText(text: String) {
    Column(verticalArrangement = Arrangement.spacedBy(12.px)) {
        parsePrivacyText(text).forEach { block ->
            when (block) {
                is PrivacyTextBlock.Paragraph -> {
                    SpanText(
                        block.text,
                        modifier = Modifier
                            .fontSize(15.px)
                            .lineHeight(1.7)
                            .color(SiteColors.textMutedLight),
                    )
                }

                is PrivacyTextBlock.BulletList -> {
                    Ul(
                        attrs = {
                            style {
                                color(SiteColors.textMutedLight)
                                margin(0.px)
                                paddingLeft(24.px)
                                property("display", "grid")
                                property("gap", "8px")
                            }
                        }
                    ) {
                        block.items.forEach { item ->
                            Li(
                                attrs = {
                                    style {
                                        fontSize(15.px)
                                        property("line-height", "1.7")
                                    }
                                }
                            ) {
                                Text(item)
                            }
                        }
                    }
                }
            }
        }
    }
}

private sealed interface PrivacyTextBlock {
    data class Paragraph(val text: String) : PrivacyTextBlock
    data class BulletList(val items: List<String>) : PrivacyTextBlock
}

private fun parsePrivacyText(text: String): List<PrivacyTextBlock> {
    val blocks = mutableListOf<PrivacyTextBlock>()
    val paragraphLines = mutableListOf<String>()
    val bulletItems = mutableListOf<String>()

    fun flushParagraph() {
        if (paragraphLines.isNotEmpty()) {
            blocks += PrivacyTextBlock.Paragraph(paragraphLines.joinToString(" "))
            paragraphLines.clear()
        }
    }

    fun flushBullets() {
        if (bulletItems.isNotEmpty()) {
            blocks += PrivacyTextBlock.BulletList(bulletItems.toList())
            bulletItems.clear()
        }
    }

    text.lines().forEach { rawLine ->
        val trimmed = rawLine.trim()
        when {
            trimmed.isEmpty() -> {
                flushBullets()
                flushParagraph()
            }

            trimmed.startsWith("- ") -> {
                flushParagraph()
                bulletItems += trimmed.removePrefix("- ").trim()
            }

            bulletItems.isNotEmpty() && rawLine.takeWhile { it == ' ' || it == '\t' }.isNotEmpty() -> {
                val lastIndex = bulletItems.lastIndex
                bulletItems[lastIndex] = bulletItems[lastIndex] + " " + trimmed
            }

            else -> {
                flushBullets()
                paragraphLines += trimmed
            }
        }
    }

    flushBullets()
    flushParagraph()
    return blocks
}
